import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { MdVerified } from 'react-icons/md';
import { BASE_API_URL } from '../helpers/Config';
import axios from 'axios';
import toast from 'react-hot-toast';

const EditProfileScreen = ({ theme = 'dark' }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        user_id: '',
        mobile: '',
        country_code: '91',
        token: localStorage.getItem('a5srf4wer7w1cd') || ''
    });

    const navigate = useNavigate();
    const isDark = theme === 'dark';

    const cardClass = isDark ? 'bg-dark text-light' : 'bg-white text-dark';
    const headerTextClass = isDark ? 'text-light' : 'text-dark';
    const mutedTextClass = isDark ? 'text-light-emphasis' : 'text-muted';
    const inputBgClass = isDark ? 'bg-dark text-light border-secondary' : 'bg-white text-dark';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const FetchProfile = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem('a5srf4wer7w1cd')
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.data) {
                setFormData(prev => ({
                    ...prev,
                    name: data.data.name || '',
                    email: data.data.email || '',
                    mobile: data.data.mobile || '',
                    country_code: data.data.country_code || '91',
                    user_id: data.data.user_id || '',
                }));
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to load profile');
        }
    };

    useEffect(() => {
        FetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const submitData = {
                name: formData.name,
                token: localStorage.getItem('a5srf4wer7w1cd')
            };

            const response = await axios.post(`${BASE_API_URL}/account/edit-profile`, submitData);
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.msg) {
                toast.success(data.msg);
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to update profile');
        }
    };

    // Get the full phone number for display
    const getFullPhoneNumber = () => {
        return `+${formData.country_code} ${formData.mobile}`;
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="h-100 overflow-auto p-2"
            style={{ maxWidth: '480px', margin: '0 auto' }}
        >
            {/* Header */}
            <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-center mb-3"
            >
                <h5 className={`mb-0 fw-bold ${headerTextClass}`}>Edit Profile</h5>
            </motion.div>

            <form onSubmit={handleSubmit}>
                {/* Profile Picture Display Only */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className={`card border-0 shadow-sm mb-3 ${cardClass}`}
                >
                    <div className="card-body text-center py-3">
                        <img
                            src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80"
                            alt="Profile"
                            className="rounded-circle object-fit-cover border border-3 border-primary shadow-sm mb-2"
                            style={{ width: '80px', height: '80px' }}
                        />
                        <div className="d-flex align-items-center justify-content-center mb-1">
                            <h6 className={`h6 fw-bold mb-0 me-1 ${headerTextClass}`}>
                                {formData?.name}
                            </h6>
                            <span className="text-primary fs-6" title="Verified">
                                <MdVerified />
                            </span>
                        </div>
                        <p className={`small mb-0 ${mutedTextClass}`}>{formData?.user_id}</p>
                    </div>
                </motion.div>

                {/* Personal Information */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={`card border-0 shadow-sm mb-3 ${cardClass}`}
                >
                    <div className="card-header bg-transparent border-0 py-2">
                        <h6 className={`mb-0 fw-bold ${headerTextClass}`}>Personal Information</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-2">
                            <div className="col-12">
                                <label className={`form-label small fw-medium ${headerTextClass}`}>
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    className={`form-control form-control-sm ${inputBgClass}`}
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="col-12">
                                <label className={`form-label small fw-medium ${headerTextClass}`}>
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    className={`form-control form-control-sm ${inputBgClass}`}
                                    name="email"
                                    value={formData.email || 'Not provided'}
                                    readOnly
                                    disabled
                                />
                                <div className="mt-1">
                                    <small className={`small ${mutedTextClass}`}>
                                        Contact <span
                                            className='text-primary fw-bold cursor-pointer'
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => navigate('../support')}
                                        >
                                            support
                                        </span> to change email address
                                    </small>
                                </div>
                            </div>
                            <div className="col-12">
                                <label className={`form-label small fw-medium ${headerTextClass}`}>
                                    Phone Number
                                </label>
                                <div className="input-group input-group-sm">
                                    <span className={`input-group-text ${isDark ? 'bg-secondary text-light border-secondary' : ''}`} style={{ minWidth: '80px' }}>
                                        +{formData.country_code}
                                    </span>
                                    <input
                                        type="tel"
                                        className={`form-control form-control-sm ${inputBgClass}`}
                                        value={formData.mobile}
                                        readOnly
                                        disabled
                                    />
                                </div>
                                <div className="mt-2">
                                    <small className={`small ${mutedTextClass}`}>
                                        Full number: {getFullPhoneNumber()}
                                    </small>
                                    <br />
                                    <small className={`small ${mutedTextClass}`}>
                                        Contact <span
                                            className='text-primary fw-bold cursor-pointer'
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => navigate('../support')}
                                        >
                                            support
                                        </span> to change phone number
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="row g-2 mb-3"
                >
                    <div className="col-6">
                        <Link
                            to="../profile"
                            className={`btn btn-outline-${isDark ? 'light' : 'secondary'} w-100 btn-sm`}
                        >
                            Cancel
                        </Link>
                    </div>
                    <div className="col-6">
                        <motion.button
                            type="submit"
                            className="btn btn-primary w-100 btn-sm"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            Save Changes
                        </motion.button>
                    </div>
                </motion.div>
            </form>
        </motion.div>
    );
};

export default EditProfileScreen;